/**
 * Ce qui est bati dans le monde, et les questions qu'on lui pose.
 *
 * POURQUOI CE FICHIER EXISTE. La liste des constructions vivait dans
 * `client/render/batiments.ts`, c'est-a-dire dans le moteur de rendu: la
 * simulation demandait au rendu ce qui existe, jusqu'a huit fois par tick
 * (collision des murs, detection d'interieur, validation d'un placement). Ca
 * marchait, et ca fermait la porte au multijoueur: le serveur qui fera autorite
 * sur le monde ne peut pas dependre d'une classe three.js. Le rendu redevient
 * donc ce qu'il doit etre — un consommateur qui affiche une liste qu'il ne
 * possede pas.
 *
 * Aucun import de three.js ni du DOM ici, comme partout dans shared/.
 */
import {
  MEUBLES_INTERACTIFS,
  aLeMeuble,
  accesEtage,
  dansEmprise,
  estAeroport,
  glissadeAuPoint,
  pisteDe,
  planEtageDe,
  portePosition,
  positionAcces,
  positionMeuble,
  positionPortable,
  positionRestaurant,
  rayonEmprise,
  surLaPiste,
  type MeubleInteractif,
} from './batiments.ts'
import { batimentDef } from '../data/batiments.ts'
import { dist } from './alea.ts'
import { numeroDeMonde } from './carte.ts'
import type { Batiment, BatimentKind } from '../types/monde.ts'
import type { Vec2 } from '../types/world.ts'

export interface MeubleProche {
  batiment: Batiment
  meuble: MeubleInteractif
  distance: number
}

/**
 * Le meuble interactif le plus proche, s'il est a portee.
 *
 * `interieurId` restreint la recherche au batiment ou l'on se trouve: sans ca,
 * on cuisinerait dans la maison du voisin a travers le mur.
 */
export function meubleProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
  interieurId: string | null,
): MeubleProche | null {
  let meilleur: MeubleProche | null = null
  for (const b of liste) {
    if (!batimentDef(b.kind).habitable) continue
    if (interieurId !== null && b.id !== interieurId) continue
    // Une seule boucle sur la table des meubles: en ecrire un par un, c'est
    // oublier le suivant qu'on ajoutera.
    for (const meuble of MEUBLES_INTERACTIFS) {
      // Ce modele-la possede-t-il ce meuble ? L'ecole n'a ni cuisine ni lit, et
      // les proposer aurait fait dormir en classe — l'infobulle doit annoncer
      // ce qui existe vraiment ici.
      if (!aLeMeuble(b.kind, meuble)) continue
      const p = positionMeuble(b, meuble)
      const d = dist(x, z, p.x, p.z)
      if (d <= portee && (!meilleur || d < meilleur.distance)) {
        meilleur = { batiment: b, meuble, distance: d }
      }
    }
  }
  return meilleur
}

/**
 * Le batiment de ce MODELE le plus proche, s'il est a portee.
 *
 * Generalise `ecoleProche` a tout batiment du village qu'on reconnait par la
 * proximite plutot que par un ancrage de meuble — le musee, la bibliotheque, et
 * ce qui viendra ensuite. Un pupitre ajoute a `ANCRAGE_MEUBLE` en aurait pose
 * un dans TOUTES les maisons du monde, y compris la tente: c'est la raison
 * pour laquelle l'ecole se reconnait ainsi, et elle ne change pas.
 */
export function villageProche(
  liste: readonly Batiment[],
  kind: BatimentKind,
  x: number,
  z: number,
  portee: number,
): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = Infinity
  for (const b of liste) {
    if (b.kind !== kind) continue
    const d = dist(x, z, b.pos.x, b.pos.z)
    // Depuis le seuil comme depuis l'interieur: la portee s'ajoute au rayon de
    // l'emprise, sinon un grand batiment ne repondrait que depuis son centre.
    if (d > portee + rayonEmprise(b)) continue
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/** La porte la plus proche, pour proposer d'entrer. */
export function porteProche(liste: readonly Batiment[], x: number, z: number, portee: number): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    if (!batimentDef(b.kind).habitable) continue
    const pp = portePosition(b)
    const d = dist(x, z, pp.x, pp.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * Le registre des constructions. Une seule verite, cote simulation.
 *
 * `liste()` est appelee plusieurs fois par tick, donc le tableau est garde en
 * cache et invalide a la pose comme au retrait — c'est la seule optimisation
 * ici, et elle est necessaire: sans elle on rallouait un tableau de plusieurs
 * dizaines d'entrees vingt fois par seconde.
 */
/**
 * La cabane a collations la plus proche, si elle est a portee.
 *
 * Il n'y en a qu'une par plage et elle sert gratuitement: on prend la premiere
 * trouvee, sans chercher la meilleure.
 */
export function cabaneProche(liste: readonly Batiment[], x: number, z: number, portee: number): Batiment | null {
  for (const b of liste) {
    if (b.kind !== 'cabane_collations') continue
    if (dist(x, z, b.pos.x, b.pos.z) <= portee) return b
  }
  return null
}

/**
 * L'ecole a portee, s'il y en a une.
 *
 * Comme la cabane a collations: c'est un batiment qu'on VISITE, et le geste se
 * fait depuis le seuil comme depuis la classe. On ne passe donc pas par les
 * ancrages de meubles, qui poseraient un pupitre dans toutes les maisons.
 */
export function ecoleProche(liste: readonly Batiment[], x: number, z: number, portee: number): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    if (b.kind !== 'ecole') continue
    const d = dist(x, z, b.pos.x, b.pos.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * L'escalier a portee, s'il y en a un.
 *
 * Il se propose des DEUX niveaux, et c'est tout son interet: en bas il fait
 * monter, en haut il fait redescendre. Une seule fonction pour les deux, sinon
 * le point vise en bas et celui vise en haut finiraient par ne plus etre au
 * meme endroit — ils sont, litteralement, la meme colonne.
 */
export function escalierProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
): Batiment | null {
  return accesProche(liste, 'escalier', x, z, portee)
}

/**
 * L'ascenseur a portee, s'il y en a un.
 *
 * Meme logique que l'escalier — la meme colonne a tous les niveaux — mais le
 * geste n'est pas le meme: l'escalier bascule entre deux niveaux, l'ascenseur
 * ouvre ses BOUTONS, parce que trois destinations ne se devinent pas d'un tap.
 */
export function ascenseurProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
): Batiment | null {
  return accesProche(liste, 'ascenseur', x, z, portee)
}

/** L'acces aux etages de ce GENRE le plus proche: c'est la donnee qui trie. */
function accesProche(
  liste: readonly Batiment[],
  genre: 'escalier' | 'ascenseur',
  x: number,
  z: number,
  portee: number,
): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    if (accesEtage(b.kind) !== genre) continue
    const p = positionAcces(b)
    const d = dist(x, z, p.x, p.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * Le portable du bureau, s'il est a portee ET qu'on est bien a l'etage.
 *
 * Le niveau est un argument obligatoire: sans lui, on ouvrirait les exercices
 * en passant sous la terrasse, a travers trois metres de dalle.
 */
export function portableProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
  niveau: number,
): Batiment | null {
  if (niveau <= 0) return null
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    // C'est le PLAN qui dit s'il y a un portable, et A QUEL niveau: celui de
    // l'hotel est au premier etage, pas au salon de lecture du deuxieme.
    const portable = planEtageDe(b.kind)?.portable
    if (!portable || portable.niveau !== niveau) continue
    const p = positionPortable(b)
    const d = dist(x, z, p.x, p.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * La table a manger d'un ETAGE, si l'on est au bon niveau et a portee.
 *
 * Meme regle que le portable: c'est le PLAN qui dit s'il y a une table
 * la-haut et a quel niveau elle est dressee — la salle a manger de l'hotel
 * est au premier etage. Une seule reponse, lue par les cibles ET par
 * l'infobulle: deux copies de la condition auraient fini par diverger.
 */
export function tableEtageProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
  niveau: number,
): Batiment | null {
  if (niveau <= 0) return null
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    const table = planEtageDe(b.kind)?.table
    if (!table || table.niveau !== niveau) continue
    const p = positionMeuble(b, 'table')
    const d = dist(x, z, p.x, p.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * LE COMPTOIR DU RESTAURANT d'un aeroport, si l'on est au bon niveau et a
 * portee.
 *
 * Meme regle que le portable et que la table de l'hotel: c'est le PLAN qui dit
 * s'il y a un restaurant et A QUEL etage. Le niveau est obligatoire — sans lui
 * on commanderait a manger depuis le hall, a travers la dalle.
 */
export function restaurantProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
  niveau: number,
): Batiment | null {
  if (niveau <= 0) return null
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    const resto = planEtageDe(b.kind)?.restaurant
    if (!resto || resto.niveau !== niveau) continue
    const p = positionRestaurant(b)
    const d = dist(x, z, p.x, p.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * L'AEROPORT designe par un point: celui dont la piste ou l'aerogare le
 * recouvre.
 *
 * C'est la reponse a deux questions qui n'en font qu'une: « peut-on batir un
 * avion ici ? » et « le pilote vient-il de designer un aeroport ou l'on se
 * pose ? ». `marge` est genereuse pour le tap — on vise du doigt.
 */
export function aeroportAuPoint(
  liste: readonly Batiment[],
  x: number,
  z: number,
  marge = 0,
): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = Infinity
  for (const b of liste) {
    const piste = pisteDe(b)
    if (!piste) continue
    const surPiste = surLaPiste(piste, x, z, marge)
    const surGare = dansEmprise(b, x, z, marge)
    if (!surPiste && !surGare) continue
    const d = Math.min(dist(x, z, piste.centre.x, piste.centre.z), dist(x, z, b.pos.x, b.pos.z))
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/** L'aeroport le plus proche d'un point, quelle que soit la distance. */
export function aeroportLePlusProche(liste: readonly Batiment[], x: number, z: number): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = Infinity
  for (const b of liste) {
    if (!estAeroport(b.kind)) continue
    const d = dist(x, z, b.pos.x, b.pos.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * L'aeroport le plus proche SITUE DANS L'AUTRE MONDE, ou null s'il n'y en a
 * aucun.
 *
 * C'est tout le voyage entre les deux iles, et il tient en une question de
 * geometrie: on ne se teleporte pas, on ne change pas de repere, on choisit
 * simplement une destination lointaine. L'appareil s'y rend ensuite avec la
 * meme machine a etats que pour l'aeroport d'a cote — transit, point d'attente
 * dans l'axe, orbite, finale — parce que sept cents metres de mer ne sont, pour
 * elle, que sept cents metres.
 *
 * Renvoyer null est une reponse a part entiere: tant que personne n'a bati
 * d'aeroport dans le monde 1, le retour n'existe pas et le bouton ne doit pas
 * s'afficher. Un geste qui n'aboutit pas est pire qu'un geste absent.
 */
export function aeroportAutreMonde(
  liste: readonly Batiment[],
  x: number,
  z: number,
  prefere?: string,
): Batiment | null {
  const ici = numeroDeMonde(x, z)
  /*
   * L'AEROPORT DE DEPART D'ABORD (`Vehicule.retour`): un avion emprunte au
   * monde 1 rentrait sinon a l'aeroport le plus proche de l'ile 2, qui n'est
   * pas forcement le sien (vu au telephone le 14 octobre). S'il a ete demoli,
   * ou s'il est dans CE monde-ci, on retombe sur la regle de toujours.
   */
  if (prefere !== undefined) {
    const b = liste.find((c) => c.id === prefere)
    if (b && estAeroport(b.kind) && numeroDeMonde(b.pos.x, b.pos.z) !== ici) return b
  }
  let meilleur: Batiment | null = null
  let meilleureD = Infinity
  for (const b of liste) {
    if (!estAeroport(b.kind)) continue
    if (numeroDeMonde(b.pos.x, b.pos.z) === ici) continue
    const d = dist(x, z, b.pos.x, b.pos.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/**
 * Le cultivable le plus proche, s'il est a portee.
 *
 * Le nom dit « jardin » par habitude, mais la question posee est « y a-t-il
 * quelque chose a cueillir ici ? », et c'est la DONNEE qui repond: tout modele
 * portant une table `recolte` est cueillable. Le test `kind !== 'jardin'` qui
 * vivait ici laissait le potager et la serre pousser pour rien — on pouvait les
 * batir, jamais y ramasser quoi que ce soit.
 */
export function jardinProche(liste: readonly Batiment[], x: number, z: number, portee: number): Batiment | null {
  let meilleur: Batiment | null = null
  let meilleureD = portee
  for (const b of liste) {
    if (!batimentDef(b.kind).recolte) continue
    const d = dist(x, z, b.pos.x, b.pos.z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = b
    }
  }
  return meilleur
}

/** Une glissade a portee: le batiment qui la porte, et son rang dans son plan. */
export interface GlissadeProche {
  batiment: Batiment
  /** Index dans `glissadesDe(kind)`: c'est lui qu'on repasse a `cheminGlissade`. */
  index: number
}

/**
 * LE HAUT D'UNE GLISSADE a portee, s'il y en a un et si l'on est bien dessus.
 *
 * Meme famille que `portableProche` et `restaurantProche`: le niveau est
 * obligatoire, et c'est le PLAN qui dit s'il y a une glissade. Proposer
 * « Glisser » depuis le bord du bassin, trois metres plus bas, serait le
 * mensonge deja paye par « Couper — Arbre » annonce a quelqu'un sur son toit.
 */
export function glissadeProche(
  liste: readonly Batiment[],
  x: number,
  z: number,
  portee: number,
  niveau: number,
): GlissadeProche | null {
  if (niveau <= 0) return null
  for (const b of liste) {
    const index = glissadeAuPoint(b, x, z, niveau, portee)
    if (index >= 0) return { batiment: b, index }
  }
  return null
}

export class Batiments {
  private parId = new Map<string, Batiment>()
  private cache: Batiment[] | null = null

  ajouter(b: Batiment): void {
    this.parId.set(b.id, b)
    this.cache = null
  }

  retirer(id: string): boolean {
    const enleve = this.parId.delete(id)
    if (enleve) this.cache = null
    return enleve
  }

  vider(): void {
    this.parId.clear()
    this.cache = null
  }

  get(id: string): Batiment | undefined {
    return this.parId.get(id)
  }

  get nombre(): number {
    return this.parId.size
  }

  liste(): Batiment[] {
    if (!this.cache) this.cache = [...this.parId.values()]
    return this.cache
  }

  ids(): string[] {
    return [...this.parId.keys()]
  }

  /**
   * A prevenir quand un batiment deja pose est MODIFIE sur place (une couleur
   * qui change, par exemple). Le registre garde des references, donc la
   * modification est deja visible; ce qu'il faut, c'est que les appelants qui
   * comparent le tableau precedent au nouveau voient bien un tableau neuf.
   */
  invalider(): void {
    this.cache = null
  }

  meubleProche(x: number, z: number, portee: number, interieurId: string | null): MeubleProche | null {
    return meubleProche(this.liste(), x, z, portee, interieurId)
  }

  porteProche(x: number, z: number, portee: number): Batiment | null {
    return porteProche(this.liste(), x, z, portee)
  }

  cabaneProche(x: number, z: number, portee: number): Batiment | null {
    return cabaneProche(this.liste(), x, z, portee)
  }

  ecoleProche(x: number, z: number, portee: number): Batiment | null {
    return ecoleProche(this.liste(), x, z, portee)
  }

  villageProche(kind: BatimentKind, x: number, z: number, portee: number): Batiment | null {
    return villageProche(this.liste(), kind, x, z, portee)
  }

  escalierProche(x: number, z: number, portee: number): Batiment | null {
    return escalierProche(this.liste(), x, z, portee)
  }

  ascenseurProche(x: number, z: number, portee: number): Batiment | null {
    return ascenseurProche(this.liste(), x, z, portee)
  }

  portableProche(x: number, z: number, portee: number, niveau: number): Batiment | null {
    return portableProche(this.liste(), x, z, portee, niveau)
  }

  tableEtageProche(x: number, z: number, portee: number, niveau: number): Batiment | null {
    return tableEtageProche(this.liste(), x, z, portee, niveau)
  }

  jardinProche(x: number, z: number, portee: number): Batiment | null {
    return jardinProche(this.liste(), x, z, portee)
  }

  /** Le haut d'une glissade a portee, avec son rang dans le plan du modele. */
  glissadeProche(x: number, z: number, portee: number, niveau: number): GlissadeProche | null {
    return glissadeProche(this.liste(), x, z, portee, niveau)
  }

  restaurantProche(x: number, z: number, portee: number, niveau: number): Batiment | null {
    return restaurantProche(this.liste(), x, z, portee, niveau)
  }

  aeroportAuPoint(x: number, z: number, marge = 0): Batiment | null {
    return aeroportAuPoint(this.liste(), x, z, marge)
  }

  aeroportLePlusProche(x: number, z: number): Batiment | null {
    return aeroportLePlusProche(this.liste(), x, z)
  }

  aeroportAutreMonde(x: number, z: number, prefere?: string): Batiment | null {
    return aeroportAutreMonde(this.liste(), x, z, prefere)
  }

  /** Position d'un batiment par identifiant, ou null. Raccourci tres demande. */
  position(id: string): Vec2 | null {
    return this.parId.get(id)?.pos ?? null
  }
}
